import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAdmin } from "../../App";
import {
  LayoutDashboard,
  Building2,
  MessageSquare,
  Users,
  LogOut,
  ShieldCheck,
  TrendingUp,
  Plus,
  ArrowRight,
  Home,
  Clock,
  FileText,
} from "lucide-react";
import { Button } from "../../components/ui/button";

const AdminDashboard = () => {
  const { admin, adminLogout, adminToken } = useAdmin();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, [adminToken]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    adminLogout();
    navigate("/admin");
  };

  const sidebarItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/admin/dashboard", active: true },
    { icon: Building2, label: "Properties", path: "/admin/properties" },
    { icon: FileText, label: "Blog", path: "/admin/blog" },
    { icon: MessageSquare, label: "Inquiries", path: "/admin/inquiries" },
  ];

  return (
    <div className="min-h-screen bg-stone-900 flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="w-64 bg-stone-800 border-r border-stone-700 flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-stone-700">
          <Link to="/admin/dashboard" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-amber-500 rounded-sm flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <div className="font-display text-lg text-white">RERA Admin</div>
              <div className="text-xs text-stone-400">Control Panel</div>
            </div>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {sidebarItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                    item.active
                      ? "bg-amber-500/10 text-amber-500 border border-amber-500/20"
                      : "text-stone-400 hover:bg-stone-700 hover:text-white"
                  }`}
                >
                  <item.icon className="w-5 h-5" strokeWidth={1.5} />
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* User Info */}
        <div className="p-4 border-t border-stone-700">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center text-white font-medium">
              {admin?.name?.charAt(0) || "A"}
            </div>
            <div>
              <p className="text-white text-sm font-medium">{admin?.name}</p>
              <p className="text-stone-400 text-xs">{admin?.email}</p>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full border-stone-600 text-stone-400 hover:bg-stone-700 hover:text-white"
          >
            <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Header */}
        <header className="bg-stone-800 border-b border-stone-700 px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-display text-2xl text-white">Dashboard</h1>
              <p className="text-stone-400 text-sm">Welcome back, {admin?.name}</p>
            </div>
            <Link to="/admin/properties/new">
              <Button className="bg-amber-500 hover:bg-amber-600 text-white">
                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Add Property
              </Button>
            </Link>
          </div>
        </header>

        {/* Stats */}
        <div className="p-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-stone-800 border border-stone-700 p-6 rounded-sm animate-pulse">
                  <div className="h-4 bg-stone-700 rounded w-1/2 mb-2" />
                  <div className="h-8 bg-stone-700 rounded w-1/3" />
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div className="bg-stone-800 border border-stone-700 p-6 rounded-sm">
                  <div className="flex items-center justify-between mb-4">
                    <Building2 className="w-8 h-8 text-emerald-500" strokeWidth={1.5} />
                    <span className="text-emerald-500 text-xs uppercase tracking-widest">Properties</span>
                  </div>
                  <p className="font-mono text-3xl text-white">{stats?.total_properties || 0}</p>
                  <p className="text-stone-400 text-sm mt-1">Total listings</p>
                </div>

                <div className="bg-stone-800 border border-stone-700 p-6 rounded-sm">
                  <div className="flex items-center justify-between mb-4">
                    <MessageSquare className="w-8 h-8 text-amber-500" strokeWidth={1.5} />
                    <span className="text-amber-500 text-xs uppercase tracking-widest">Inquiries</span>
                  </div>
                  <p className="font-mono text-3xl text-white">{stats?.total_inquiries || 0}</p>
                  <p className="text-stone-400 text-sm mt-1">Total received</p>
                </div>

                <div className="bg-stone-800 border border-stone-700 p-6 rounded-sm">
                  <div className="flex items-center justify-between mb-4">
                    <Clock className="w-8 h-8 text-red-500" strokeWidth={1.5} />
                    <span className="text-red-500 text-xs uppercase tracking-widest">Pending</span>
                  </div>
                  <p className="font-mono text-3xl text-white">{stats?.pending_inquiries || 0}</p>
                  <p className="text-stone-400 text-sm mt-1">Need attention</p>
                </div>

                <div className="bg-stone-800 border border-stone-700 p-6 rounded-sm">
                  <div className="flex items-center justify-between mb-4">
                    <Users className="w-8 h-8 text-blue-500" strokeWidth={1.5} />
                    <span className="text-blue-500 text-xs uppercase tracking-widest">Users</span>
                  </div>
                  <p className="font-mono text-3xl text-white">{stats?.total_users || 0}</p>
                  <p className="text-stone-400 text-sm mt-1">Registered</p>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* By Category */}
                <div className="bg-stone-800 border border-stone-700 p-6 rounded-sm">
                  <h3 className="font-display text-lg text-white mb-4">Properties by Category</h3>
                  <div className="space-y-3">
                    {Object.entries(stats?.by_category || {}).map(([category, count]) => (
                      <div key={category} className="flex items-center justify-between">
                        <span className="text-stone-400 capitalize">{category.replace("_", " ")}</span>
                        <span className="font-mono text-white">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* By City */}
                <div className="bg-stone-800 border border-stone-700 p-6 rounded-sm">
                  <h3 className="font-display text-lg text-white mb-4">Properties by City</h3>
                  <div className="space-y-3">
                    {Object.entries(stats?.by_city || {}).map(([city, count]) => (
                      <div key={city} className="flex items-center justify-between">
                        <span className="text-stone-400">{city}</span>
                        <span className="font-mono text-white">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                <Link
                  to="/admin/properties"
                  className="bg-stone-800 border border-stone-700 p-6 rounded-sm hover:border-emerald-500/50 transition-colors group"
                >
                  <Building2 className="w-8 h-8 text-emerald-500 mb-4" strokeWidth={1.5} />
                  <h3 className="font-medium text-white mb-1">Manage Properties</h3>
                  <p className="text-stone-400 text-sm mb-4">Add, edit, or remove property listings</p>
                  <span className="text-emerald-500 text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                    Go to Properties <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                  </span>
                </Link>

                <Link
                  to="/admin/properties/new"
                  className="bg-stone-800 border border-stone-700 p-6 rounded-sm hover:border-amber-500/50 transition-colors group"
                >
                  <Plus className="w-8 h-8 text-amber-500 mb-4" strokeWidth={1.5} />
                  <h3 className="font-medium text-white mb-1">Add New Property</h3>
                  <p className="text-stone-400 text-sm mb-4">Create a new project listing</p>
                  <span className="text-amber-500 text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                    Add Property <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                  </span>
                </Link>

                <Link
                  to="/admin/inquiries"
                  className="bg-stone-800 border border-stone-700 p-6 rounded-sm hover:border-blue-500/50 transition-colors group"
                >
                  <MessageSquare className="w-8 h-8 text-blue-500 mb-4" strokeWidth={1.5} />
                  <h3 className="font-medium text-white mb-1">View Inquiries</h3>
                  <p className="text-stone-400 text-sm mb-4">Manage customer inquiries and leads</p>
                  <span className="text-blue-500 text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                    View Inquiries <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                  </span>
                </Link>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
